import { Component, OnDestroy, computed, effect, input, output, signal } from '@angular/core';
import { Lux } from '@roosevelt/common-ui-lib/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { IAuthInfo, ILoginState } from '../login/store/state';
import { getUserInfo } from '../login/store/selectors';
import { createFeatureManagedClientSearchController, provideClientSearchStatePrefix } from '../shared/client-search-render.controller';

const BILLING_CLIENT_SEARCH_PREFIX = 'billing-';

export type BillingClientSelection = {
  specifiedId: string;
  name: string;
  planAcronym: string;
};

export type BillingSubGroupSelection = BillingClientSelection & {
  client: BillingClientSelection;
};

export type BillingEbillContext = {
  payer: string;
  groupIdentifier: string;
  subgroupIdentifier: string;
};

type BillingFeed = {
  clientId: string;
  clientName: string;
  subClientId: string;
  subClientName: string;
  plan: string;
  adminPlan: string;
};

type BillingUserFeed = {
  email: string;
  userName: string;
  appName: string;
};

@Component({
  selector: 'app-billing-page',
  templateUrl: 'billing.component.html',
  styleUrls: ['billing.component.scss'],
  providers: [provideClientSearchStatePrefix(BILLING_CLIENT_SEARCH_PREFIX)],
  standalone: false
})
export class BillingComponent implements OnDestroy {
  readonly luxId = 'bmt-billing' + this.constructor.name;
  readonly insertEbillContext = output<BillingEbillContext>();
  readonly selectedAdminPlan = input('');
  readonly ebillResetVersion = input(0);
  readonly activeTab = signal('Invoices');
  private readonly clientSearchResetVersion = signal(0);
  readonly clientSearchMountKey = computed(() => {
    const mode = this.activeTab() === 'Invoices' || this.activeTab() === 'Historical Reports' ? 'reports' : 'payments';
    return `${this.selectedAdminPlan()}|${this.activeTab()}|${mode}|${this.clientSearchResetVersion()}`;
  });
  readonly selectedClient = signal<BillingClientSelection | null>(null);
  readonly selectedSubClient = signal<string | undefined>(undefined);
  readonly ebillError = input('');
  readonly showClientSubclientInfo = signal(false);
  readonly subGroupInfo = signal<BillingSubGroupSelection[] | undefined>(undefined);
  readonly allSubClients = signal(false);
  readonly billingFeed = signal<BillingFeed | undefined>(undefined);
  readonly showReports = signal(false);
  readonly showPaymentHistory = signal(false);
  readonly billingFeatures = signal<string[]>([]);
  readonly billingAttributeFeatures = input<string[]>([]);

  private readonly userInfo = toSignal(this.lux.get(getUserInfo, this.luxId), { initialValue: null as IAuthInfo | null });
  readonly clientSearch = createFeatureManagedClientSearchController({
    lux: this.lux,
    storagePrefix: BILLING_CLIENT_SEARCH_PREFIX,
    selectedAdminPlan: this.selectedAdminPlan,
    remountTrigger: this.clientSearchMountKey,
    canRenderWhenAuthorized: () => this.activeTab() !== 'My Reports'
  });
  readonly userFeed = computed<BillingUserFeed | null>(() => {
    const info = this.userInfo();
    return info ? {
      email: info.email,
      userName: info.id,
      appName: 'RBMT'
    } : null;
  });
  private lastHandledEbillResetVersion = 0;

  constructor(private readonly lux: Lux<{ login: ILoginState; authInfo: IAuthInfo; clientSearch: unknown }>) {
    effect(() => {
      const resetVersion = this.ebillResetVersion();
      if (resetVersion <= this.lastHandledEbillResetVersion) {
        return;
      }

      this.lastHandledEbillResetVersion = resetVersion;
      this.resetSelected();
    });
  }

  clientSelected(event: unknown): void {
    this.showReports.set(false);
    this.showPaymentHistory.set(false);
    this.showClientSubclientInfo.set(false);
    this.selectedClient.set(event as BillingClientSelection | null);
    this.selectedSubClient.set(undefined);
    this.subGroupInfo.set(undefined);
    this.allSubClients.set(false);
    this.billingFeed.set(undefined);
  }

  subClientSelected(subGroup: unknown): void {
    this.showReports.set(false);
    this.showPaymentHistory.set(false);

    const resolvedSubGroups = subGroup as BillingSubGroupSelection[] | null | undefined;

    if (!resolvedSubGroups?.length) {
      return;
    }

    this.selectedSubClient.set(resolvedSubGroups[0].specifiedId);
    if (this.activeTab() === 'Recurring ACH Setup' || this.activeTab() === 'PaymentHistory') {
      this.subGroupInfo.set(resolvedSubGroups);
      this.allSubClients.set(false);
      this.showClientSubclientInfo.set(true);
      if (this.activeTab() === 'PaymentHistory') {
        this.constructBillingFeed(resolvedSubGroups[0]);
      }
      return;
    }

    if (this.activeTab() === 'EBill') {
      this.insertEbillContext.emit({
        payer: resolvedSubGroups[0].planAcronym,
        groupIdentifier: resolvedSubGroups[0].client.specifiedId,
        subgroupIdentifier: resolvedSubGroups[0].specifiedId
      });
      return;
    }

    if (this.activeTab() === 'Invoices' || this.activeTab() === 'Historical Reports') {
      this.constructBillingFeed(resolvedSubGroups[0]);
    }
  }

  constructBillingFeed(data?: BillingSubGroupSelection): void {
    this.billingFeatures.set(this.activeTab() === 'Invoices'
      ? ['consolidatedInvoice', 'nonConsolidatedInvoice']
      : ['historicalReports']);

    if (data) {
      this.billingFeed.set({
        clientId: data.client.specifiedId,
        clientName: data.client.name,
        subClientId: data.specifiedId,
        subClientName: data.name,
        plan: data.planAcronym,
        adminPlan: this.selectedAdminPlan()
      });
    } else if (this.selectedClient()) {
      const selectedClient = this.selectedClient();
      this.billingFeed.set({
        clientId: selectedClient.specifiedId,
        clientName: selectedClient.name,
        subClientId: '',
        subClientName: '',
        plan: selectedClient.planAcronym,
        adminPlan: this.selectedAdminPlan()
      });
    } else {
      this.billingFeed.set(undefined);
    }

    this.showPaymentHistory.set(this.activeTab() === 'PaymentHistory');
    this.showReports.set(!!this.billingFeed());
  }

  handleClientSearchReset(): void {
    this.clearBillingSelectionState(true);
  }

  resetSelected(): void {
    this.clearBillingSelectionState(true);
    this.clientSearchResetVersion.update(version => version + 1);
  }

  private clearBillingSelectionState(clearSelectedClient = false): void {
    if (clearSelectedClient) {
      this.selectedClient.set(null);
    }
    this.showClientSubclientInfo.set(false);
    this.showReports.set(false);
    this.showPaymentHistory.set(false);
    this.subGroupInfo.set(undefined);
    this.selectedSubClient.set(undefined);
    this.allSubClients.set(false);
    this.billingFeed.set(undefined);
  }

  switchBillingTab(tab: string): void {
    const isSameTab = this.activeTab() === tab;

    this.activeTab.set(tab);
    this.selectedClient.set(null);
    this.clearBillingSelectionState();

    if (isSameTab) {
      this.clientSearchResetVersion.update(version => version + 1);
    }
  }

  selectedAllSubClients(event: unknown): void {
    if (event !== true) {
      return;
    }

    this.showClientSubclientInfo.set(this.activeTab() === 'Recurring ACH Setup' || this.activeTab() === 'PaymentHistory');
    this.allSubClients.set(true);
    this.showReports.set(false);
    this.constructBillingFeed();
  }

  ngOnDestroy(): void {
    this.clientSearch.destroy();
    this.lux.destroy(this.luxId);
  }
}
